import {
  ACTIVE_PAYMENT_METHODS,
  currenciesForMethod,
  isCurrencyAllowedForMethod,
  methodsForCurrency,
} from "@pwz/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, Check, Paperclip, ReceiptText, TriangleAlert, Wallet, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiClient } from "../../../api/ApiClientContext.js";
import { ApiClientError } from "../../../api/errors.js";
import type {
  DealContext,
  FxQuote,
  LedgerMethod,
  LedgerPayment,
  OriginalCurrency,
} from "../../../api/schemas.js";
import { compressProofImage } from "../../../lib/image-compress.js";
import {
  fromMinor,
  METHOD_ICONS,
  METHOD_LABELS,
  PROOF_EXPECTED_METHODS,
  toMinor,
} from "../../../lib/money.js";
import { formatInTimeZone, formatTimeInZone, getDeviceTimeZone } from "../../../lib/timezone.js";
import type { AccessibleError } from "../../admin/model/types.js";

export interface DealMoneyPanelProps {
  readonly leadId: number;
  readonly context: DealContext | undefined;
  readonly onError: (error: AccessibleError) => void;
}

const STATUS_LABELS: Record<string, string> = {
  not_paid: "Не оплачено",
  advance: "Аванс",
  partial: "Частично",
  paid: "Оплачено",
  overpaid: "Переплата",
  unknown: "Нет данных",
};

function money(amount: number | null, currency: string | null): string {
  if (amount === null) return "—";
  return `${amount.toLocaleString("ru-RU")}${currency ? ` ${currency}` : ""}`;
}

function newKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function rate(value: number): string {
  return value.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

/** «1 USDT = 17 757 IDR · open.er-api.com, 12.08 14:00» — курс виден до отправки (§5). */
function quoteLine(quote: FxQuote): string {
  const when = quote.fetchedAt.slice(0, 16).replace("T", " ");
  return `1 ${quote.base} = ${rate(quote.rate)} ${quote.quote} · ${quote.source}, ${when}${quote.stale ? " · курс устарел" : ""}`;
}

function paymentMoment(payment: LedgerPayment): string {
  const iso = payment.acceptedAt ?? payment.createdAt;
  const zone = getDeviceTimeZone();
  return `${formatInTimeZone(iso, zone, { day: "numeric", month: "short" })} ${formatTimeInZone(iso, zone)}`;
}

/**
 * Одна операция выписки.
 *
 * Читается как строка банковской истории: слева способ значком, крупно — что принесли, ниже
 * мелким — во что зачлось, чем и когда. Чек висит на самой операции: раньше он уезжал в amo и
 * доказательство существовало где угодно, только не там, где по нему принимают решение.
 */
function StatementRow(props: {
  readonly payment: LedgerPayment;
  readonly busy: boolean;
  readonly onReview: (decision: "confirmed" | "rejected") => void;
}) {
  const payment = props.payment;
  const Icon = METHOD_ICONS[payment.method] ?? Banknote;
  const original = fromMinor(payment.amountOriginalMinor, payment.currencyOriginal);
  const converted = payment.currencyOriginal !== payment.currency;
  const refund = payment.operationType === "refund";
  const proofMissing = payment.receiptUrl === null && PROOF_EXPECTED_METHODS.has(payment.method);

  return (
    <li
      className="flex min-w-0 gap-3 px-3 py-3 first:pt-0 last:pb-0"
      data-testid={`deal-money-payment-${payment.id}`}
    >
      <span
        className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${
          payment.status === "confirmed"
            ? "bg-ok-100 text-ok-500"
            : payment.status === "rejected"
              ? "bg-ink-100 text-ink-500"
              : "bg-warn-100 text-warn-500"
        }`}
        aria-hidden="true"
      >
        <Icon className="size-4" />
      </span>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex min-w-0 items-baseline justify-between gap-2">
          <span className="pwz-readout min-w-0 truncate font-semibold text-ink-950">
            {refund ? "−" : "+"}
            {original.toLocaleString("ru-RU")} {payment.currencyOriginal}
          </span>
          <Badge
            variant={payment.status === "confirmed" ? "default" : "outline"}
            className="shrink-0"
            data-testid={`deal-money-payment-status-${payment.id}`}
          >
            {payment.status === "confirmed"
              ? "Подтверждено"
              : payment.status === "rejected"
                ? "Отклонено"
                : "Ожидает"}
          </Badge>
        </div>

        <p className="min-w-0 break-words text-ink-600 text-xs">
          {converted ? (
            <>
              <span className="pwz-readout">
                → {payment.amountLocal.toLocaleString("ru-RU")} {payment.currency}
              </span>
              {" · "}
            </>
          ) : null}
          {METHOD_LABELS[payment.method]}
          {" · "}
          {paymentMoment(payment)}
          {payment.acceptorRole === "instructor" ? " · от инструктора" : ""}
          {refund ? " · возврат" : ""}
          {payment.rateStale ? " · курс устарел" : ""}
        </p>

        {payment.comment ? (
          <p className="min-w-0 break-words text-ink-700 text-xs">{payment.comment}</p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {payment.receiptUrl ? (
            <Button asChild size="sm" variant="outline">
              <a
                href={payment.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`deal-money-proof-${payment.id}`}
              >
                <Paperclip aria-hidden="true" />
                Чек
              </a>
            </Button>
          ) : proofMissing ? (
            // Перевод без чека — не ошибка отправки, а долг по доказательству: видно сразу,
            // у кого его спрашивать.
            <span
              className="inline-flex items-center gap-1 text-warn-500 text-xs"
              data-testid={`deal-money-noproof-${payment.id}`}
            >
              <TriangleAlert className="size-3.5" aria-hidden="true" />
              без чека
            </span>
          ) : null}

          {payment.status === "pending" ? (
            <span className="ml-auto flex gap-2">
              <Button
                type="button"
                size="sm"
                data-testid={`deal-money-confirm-${payment.id}`}
                disabled={props.busy}
                onClick={() => props.onReview("confirmed")}
              >
                <Check aria-hidden="true" />
                Подтвердить
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid={`deal-money-reject-${payment.id}`}
                disabled={props.busy}
                onClick={() => props.onReview("rejected")}
              >
                <X aria-hidden="true" />
                Отклонить
              </Button>
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/**
 * Деньги сделки в одном месте: контракт, баланс и поступления (§7.2 docs/amo-architecture.md).
 *
 * Менеджер здесь — принимающая сторона, поэтому его поступление подтверждается сразу; приём от
 * инструктора приходит сюда же со статусом «Ожидает» и ждёт решения. Курс и наценка не спрашиваются
 * без нужды: сервер берёт их из источника курсов и настроек финансового блока.
 *
 * Раскладка считается от узкой шторки: поля идут сеткой, а не строкой с фиксированными ширинами —
 * на 470 px такая строка наезжала сама на себя.
 */
export function DealMoneyPanel(props: DealMoneyPanelProps) {
  const api = useApiClient();
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [amountPrefilled, setAmountPrefilled] = useState(false);
  const [currency, setCurrency] = useState<OriginalCurrency | null>(null);
  const [method, setMethod] = useState<LedgerMethod>("cash");
  const [ownRate, setOwnRate] = useState("");
  const [allowOverpay, setAllowOverpay] = useState(false);
  /** Форма приёма свёрнута, пока её не открыли: баланс и выписка важнее пустых полей. */
  const [paymentFormOpen, setPaymentFormOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [proof, setProof] = useState<File | null>(null);

  const ledgerQuery = useQuery({
    queryKey: ["deal-ledger", props.leadId],
    queryFn: () => api.getDealLedger(props.leadId),
  });

  const dealCurrency = ledgerQuery.data?.currency ?? null;
  const selectedCurrency: OriginalCurrency | null = currency ?? dealCurrency;

  // §7.4: список валют собирается из валюты сделки — касса другого острова не предлагается.
  const currencyOptions: OriginalCurrency[] = dealCurrency
    ? [dealCurrency, "USD", "USDT", "RUB"]
    : ["USD", "USDT", "RUB"];
  // Способ предлагается только тот, которым эта валюта ходит: крипта — USDT, сертификат —
  // деньги площадки, наличные и перевод — всё остальное (`METHOD_CURRENCIES` в контрактах).
  const methodOptions: readonly LedgerMethod[] =
    selectedCurrency === null ? ACTIVE_PAYMENT_METHODS : methodsForCurrency(selectedCurrency);

  // Сумма предзаполняется долгом один раз: цифра-подсказка, а не замок.
  const debt = ledgerQuery.data?.balance.debtLocal ?? null;
  useEffect(() => {
    if (amountPrefilled || debt === null || debt <= 0) return;
    setAmount(String(debt));
    setAmountPrefilled(true);
  }, [amountPrefilled, debt]);

  // Курс виден до отправки: прямая котировка для USDT, цепочка для RUB.
  const usdtLocalQuery = useQuery({
    queryKey: ["fx", "USDT", dealCurrency],
    queryFn: () => api.getFxQuote(dealCurrency as string),
    enabled: dealCurrency !== null,
    staleTime: 60_000,
  });
  const usdtRubQuery = useQuery({
    queryKey: ["fx", "USDT", "RUB"],
    queryFn: () => api.getFxQuote("RUB"),
    enabled: selectedCurrency === "RUB",
    staleTime: 60_000,
  });
  const financeQuery = useQuery({
    queryKey: ["admin", "finance-settings"],
    queryFn: () => api.getFinanceSettings(),
    staleTime: 60_000,
  });

  const fail = (err: unknown, fallback: string) => {
    const correlationId = err instanceof ApiClientError ? err.correlationId : undefined;
    props.onError({
      id: `money-${correlationId || Date.now()}`,
      message: err instanceof Error ? err.message : fallback,
      code: err instanceof ApiClientError ? err.code : "client_error",
      ...(correlationId ? { correlationId } : {}),
    });
  };

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["deal-ledger", props.leadId] });
    // The amo card is the other half of this panel; a manager who just took money looks there next.
    await api.refreshDealShowcase(props.leadId).catch(() => undefined);
  };

  const contractMutation = useMutation({
    mutationFn: (reason?: string) => {
      const lines = (props.context?.courses ?? []).map((course) => ({
        courseElementId: course.elementId,
        quantity: Math.max(1, Math.round(course.quantity)),
      }));
      return api.createLedgerContract({
        leadId: props.leadId,
        lines,
        ...(reason !== undefined ? { reason } : {}),
        idempotencyKey: newKey(`contract-${props.leadId}`),
      });
    },
    onSuccess: refresh,
    onError: (err) => fail(err, "Не удалось оформить контракт"),
  });

  const paymentMutation = useMutation({
    mutationFn: async () => {
      const parsed = Number(amount.replace(",", "."));
      const parsedRate = Number(ownRate.replace(",", "."));
      const code = selectedCurrency as OriginalCurrency;
      // Чек уезжает первым: без ссылки на него подтверждать перевод нечем, и точно так же
      // это делает инструктор — доказательство одно на оба входа.
      let receiptUrl: string | null = null;
      if (proof) {
        const compressed = await compressProofImage(proof);
        const uploaded = await api.uploadLedgerReceipt({
          leadId: props.leadId,
          file: compressed,
          fileName: compressed.name,
        });
        receiptUrl = uploaded.url;
      }
      return api.submitLedgerPayment({
        leadId: props.leadId,
        amountOriginalMinor: toMinor(parsed, code),
        currencyOriginal: code,
        method,
        // The manager is the acceptor, so there is nobody left to review it (§7.2).
        autoConfirm: true,
        ...(ownRate.trim() !== "" && Number.isFinite(parsedRate) && parsedRate > 0
          ? { rateToLocal: parsedRate }
          : {}),
        ...(allowOverpay ? { allowOverpay: true } : {}),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(receiptUrl ? { receiptUrl } : {}),
        idempotencyKey: newKey(`payment-${props.leadId}`),
      });
    },
    onSuccess: async () => {
      setPaymentFormOpen(false);
      setAmount("");
      setComment("");
      setOwnRate("");
      setAllowOverpay(false);
      setProof(null);
      await refresh();
    },
    onError: (err) => fail(err, "Не удалось записать поступление"),
  });

  const reviewMutation = useMutation({
    mutationFn: (input: { paymentId: string; decision: "confirmed" | "rejected" }) =>
      api.reviewLedgerPayment(input.paymentId, {
        decision: input.decision,
        idempotencyKey: newKey(`review-${input.paymentId}-${input.decision}`),
      }),
    onSuccess: refresh,
    onError: (err) => fail(err, "Не удалось провести решение по поступлению"),
  });

  const ledger = ledgerQuery.data;
  const amountValid = amount.trim() !== "" && Number(amount.replace(",", ".")) > 0;
  const linkedCourses = props.context?.courses ?? [];

  // Докупка: контракт — снимок, и он не видит курс, привязанный к сделке после подписания.
  // Пока снимок не переоформлен, «Начислено» стоит на месте и долг по новому курсу равен нулю.
  const contractQuantities = new Map(
    (ledger?.contract?.lines ?? []).map((line) => [line.courseElementId, line.quantity]),
  );
  const staleContract =
    ledger?.contract != null &&
    linkedCourses.length > 0 &&
    (linkedCourses.some(
      (course) =>
        (contractQuantities.get(course.elementId) ?? 0) !==
        Math.max(1, Math.round(course.quantity)),
    ) ||
      contractQuantities.size !== linkedCourses.length);
  const addedCourses = staleContract
    ? linkedCourses.filter((course) => !contractQuantities.has(course.elementId))
    : [];

  if (ledgerQuery.isLoading) {
    return <Skeleton className="h-40 w-full rounded-2xl" data-testid="deal-money-loading" />;
  }

  return (
    <section className="space-y-3" data-testid="deal-money">
      {/* Баланс. Долг — единственное число, ради которого сюда заходят, поэтому он крупный,
          а остальные три уходят тихой строкой: четыре равные колонки на 470 px сталкивались. */}
      <div className="pwz-sheet p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="pwz-heading flex items-center gap-2 text-base">
            <Wallet className="size-4 text-ink-500" aria-hidden="true" />
            Деньги сделки
          </h2>
          {ledger ? (
            <Badge variant="outline" data-testid="deal-money-status">
              {STATUS_LABELS[ledger.balance.paymentStatus] ?? ledger.balance.paymentStatus}
            </Badge>
          ) : null}
        </div>

        {ledger ? (
          <dl className="mt-3">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="pwz-eyebrow">Долг</dt>
              <dd
                className="pwz-readout min-w-0 truncate font-semibold text-ink-950 text-xl leading-none"
                data-testid="deal-money-debt"
              >
                {money(ledger.balance.debtLocal, ledger.currency)}
              </dd>
            </div>
            <div className="mt-2.5 grid grid-cols-3 gap-3 border-t border-pad-200 pt-2 text-xs">
              <div className="flex min-w-0 flex-col gap-0.5">
                <dt className="text-ink-600">Начислено</dt>
                <dd className="pwz-readout truncate text-ink-800" data-testid="deal-money-charged">
                  {money(ledger.balance.chargedLocal, ledger.currency)}
                </dd>
              </div>
              <div className="flex min-w-0 flex-col gap-0.5">
                <dt className="text-ink-600">Внесено</dt>
                <dd className="pwz-readout truncate text-ink-800" data-testid="deal-money-paid">
                  {money(ledger.balance.paidLocal, ledger.currency)}
                </dd>
              </div>
              <div className="flex min-w-0 flex-col gap-0.5">
                <dt className="text-ink-600">Ожидает</dt>
                <dd className="pwz-readout truncate text-ink-800" data-testid="deal-money-pending">
                  {money(ledger.balance.pendingLocal, ledger.currency)}
                </dd>
              </div>
            </div>
          </dl>
        ) : null}
      </div>

      {ledger?.contract === null ? (
        <div className="pwz-sheet space-y-2 p-4">
          <p className="pwz-eyebrow flex items-center gap-1.5">
            <ReceiptText className="size-3.5" aria-hidden="true" />
            Контракт
          </p>
          <p
            className="text-ink-600 text-sm"
            title="Оформление зафиксирует цены и курс, свяжет товары и запишет бюджет сделки"
          >
            Не оформлен — цены пока берутся с карточек курсов.
          </p>
          <ul className="pwz-readout space-y-1 text-ink-600 text-xs">
            {linkedCourses.map((course) => (
              <li key={course.elementId}>
                {course.name ?? `Курс ${course.elementId}`} ·{" "}
                {money(course.priceLocal, ledger.currency)}
                {course.quantity > 1 ? ` × ${course.quantity}` : ""}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            className="w-full"
            data-testid="deal-money-contract"
            disabled={linkedCourses.length === 0 || contractMutation.isPending}
            onClick={() => contractMutation.mutate(undefined)}
          >
            {contractMutation.isPending ? "Оформляем…" : "Оформить контракт"}
          </Button>
          {linkedCourses.length === 0 ? (
            <p className="text-destructive text-xs">
              В сделке нет привязанных курсов — добавьте товар каталога «Курсы» в amoCRM.
            </p>
          ) : null}
        </div>
      ) : ledger?.contract ? (
        <>
          <p
            className="flex gap-1.5 px-1 text-ink-600 text-xs"
            data-testid="deal-money-contract-info"
          >
            <ReceiptText className="mt-px size-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0">
              Контракт от {ledger.contract.createdAt.slice(0, 10)} · курс{" "}
              <span className="pwz-readout">
                {ledger.contract.usdtRate.toLocaleString("ru-RU")}
              </span>{" "}
              {ledger.contract.currency} за 1 USDT · бюджет{" "}
              <span className="pwz-readout">{ledger.contract.charge.budgetUsdt}</span> USDT
            </span>
          </p>
          {staleContract ? (
            <div
              className="space-y-2 rounded-2xl bg-warn-100 p-3"
              data-testid="deal-money-contract-stale"
            >
              <p className="flex gap-2 text-ink-900 text-sm">
                <TriangleAlert
                  className="mt-0.5 size-4 shrink-0 text-warn-500"
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  Состав сделки изменился, а контракт этого ещё не видит — «Начислено» и долг
                  считаются по старому снимку.
                </span>
              </p>
              {addedCourses.length > 0 ? (
                <ul className="pwz-readout space-y-1 text-ink-600 text-xs">
                  {addedCourses.map((course) => (
                    <li key={course.elementId}>
                      + {course.name ?? `Курс ${course.elementId}`} ·{" "}
                      {money(course.priceLocal, ledger.currency)}
                      {course.quantity > 1 ? ` × ${course.quantity}` : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
              <Button
                type="button"
                className="w-full"
                data-testid="deal-money-contract-reissue"
                disabled={contractMutation.isPending}
                onClick={() => contractMutation.mutate("Состав сделки изменился (докупка)")}
              >
                {contractMutation.isPending ? "Переоформляем…" : "Переоформить контракт"}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Форма приёма раскрывается по кнопке. Раньше она стояла раскрытой всегда — семь полей
          между балансом и выпиской, — и шторка читалась как анкета, а не как состояние сделки. */}
      {!paymentFormOpen ? (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          data-testid="deal-money-open-form"
          onClick={() => setPaymentFormOpen(true)}
        >
          <Banknote className="size-4" aria-hidden="true" />
          Принять оплату
        </Button>
      ) : (
        <div className="pwz-sheet space-y-3 p-4" data-testid="deal-money-form">
          <div className="flex items-center justify-between gap-2">
            <p className="pwz-eyebrow flex items-center gap-1.5">
              <Banknote className="size-3.5" aria-hidden="true" />
              Принять оплату
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="deal-money-close-form"
              onClick={() => setPaymentFormOpen(false)}
            >
              Свернуть
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0">
              <Label htmlFor="deal-money-amount" className="text-ink-600 text-xs">
                Сумма
              </Label>
              <Input
                id="deal-money-amount"
                data-testid="deal-money-amount"
                inputMode="decimal"
                className="mt-1 h-10 text-right"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
            {/* Общий Select, а не нативный <select>: два списка в одной форме не должны выглядеть
              как из разных приложений, и клавиатурное поведение тоже одно на всё. */}
            <div className="min-w-0">
              <Label htmlFor="deal-money-currency" className="text-ink-600 text-xs">
                Валюта
              </Label>
              <Select
                value={selectedCurrency ?? ""}
                onValueChange={(value) => {
                  const code = value as OriginalCurrency;
                  setCurrency(code);
                  // Смена валюты уводит способ, которым она не ходит: доллар криптой не берут.
                  if (!isCurrencyAllowedForMethod(method, code)) {
                    setMethod(methodsForCurrency(code)[0] ?? "cash");
                  }
                }}
              >
                <SelectTrigger
                  id="deal-money-currency"
                  className="mt-1 h-10 w-full"
                  data-testid="deal-money-currency"
                  aria-label="Валюта приёма"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencyOptions.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 min-w-0">
              <Label htmlFor="deal-money-method" className="text-ink-600 text-xs">
                Способ
              </Label>
              <Select
                value={method}
                onValueChange={(value) => {
                  const next = value as LedgerMethod;
                  setMethod(next);
                  // И обратно: выбрал крипту — валюта становится USDT, а не остаётся рублём.
                  if (
                    selectedCurrency !== null &&
                    !isCurrencyAllowedForMethod(next, selectedCurrency)
                  ) {
                    setCurrency(currenciesForMethod(next)[0] ?? dealCurrency);
                  }
                }}
              >
                <SelectTrigger
                  id="deal-money-method"
                  className="mt-1 h-10 w-full"
                  data-testid="deal-money-method"
                  aria-label="Способ оплаты"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {methodOptions.map((key) => {
                    const Icon = METHOD_ICONS[key];
                    return (
                      <SelectItem key={key} value={key}>
                        <Icon className="size-4 text-ink-500" aria-hidden="true" />
                        {METHOD_LABELS[key]}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {selectedCurrency !== null && selectedCurrency !== dealCurrency ? (
              <div className="col-span-2 min-w-0">
                <Label htmlFor="deal-money-rate" className="text-ink-600 text-xs">
                  Свой курс
                </Label>
                <Input
                  id="deal-money-rate"
                  data-testid="deal-money-rate"
                  inputMode="decimal"
                  className="mt-1 h-10 text-right"
                  placeholder="рыночный"
                  value={ownRate}
                  onChange={(event) => setOwnRate(event.target.value)}
                />
              </div>
            ) : null}

            <div className="col-span-2 min-w-0">
              <Label htmlFor="deal-money-comment" className="text-ink-600 text-xs">
                Комментарий
              </Label>
              <Input
                id="deal-money-comment"
                data-testid="deal-money-comment"
                className="mt-1 h-10"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </div>

            {/* Чек прикладывает и менеджер, не только инструктор: доказательство должно быть у
              каждого перевода, кто бы его ни принял. Наличные подтверждаются передачей денег. */}
            <div className="col-span-2 min-w-0">
              <Label htmlFor="deal-money-proof" className="text-ink-800">
                <Paperclip className="size-3.5" aria-hidden="true" />
                Чек{" "}
                <span className="font-normal text-ink-500">
                  {PROOF_EXPECTED_METHODS.has(method)
                    ? "— приложите скриншот перевода"
                    : "— необязательно"}
                </span>
              </Label>
              <Input
                id="deal-money-proof"
                data-testid="deal-money-proof"
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                className="mt-1 h-10 py-2 text-sm file:mr-3 file:h-6 file:rounded-full file:bg-pad-100 file:px-3 file:text-ink-800"
                onChange={(event) => setProof(event.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          {/* Курс не прячется за отправкой: менеджер видит, по чём зачтётся, до «Записать». */}
          {selectedCurrency === "USDT" && usdtLocalQuery.data ? (
            <p className="pwz-readout text-ink-600 text-xs" data-testid="deal-money-fx">
              {quoteLine(usdtLocalQuery.data)}
            </p>
          ) : null}
          {selectedCurrency === "RUB" && dealCurrency ? (
            <div className="space-y-0.5 text-ink-600 text-xs" data-testid="deal-money-fx">
              {usdtRubQuery.data ? (
                <p className="pwz-readout">{quoteLine(usdtRubQuery.data)}</p>
              ) : null}
              {usdtLocalQuery.data ? (
                <p className="pwz-readout">{quoteLine(usdtLocalQuery.data)}</p>
              ) : null}
              {usdtRubQuery.data && usdtLocalQuery.data ? (
                <p data-testid="deal-money-fx-effective">
                  {(() => {
                    const parsedOwn = Number(ownRate.replace(",", "."));
                    const base =
                      ownRate.trim() !== "" && Number.isFinite(parsedOwn) && parsedOwn > 0
                        ? parsedOwn
                        : usdtLocalQuery.data.rate / usdtRubQuery.data.rate;
                    const markup = financeQuery.data?.currencySurcharges.RUB ?? 0;
                    const effective = base * (1 - markup / 100);
                    return `1 RUB = ${rate(base)} ${dealCurrency}; наценка ${markup} % → эффективно ${rate(effective)} ${dealCurrency}`;
                  })()}
                </p>
              ) : null}
            </div>
          ) : null}

          <label className="flex items-center gap-2 text-ink-600 text-xs">
            <input
              type="checkbox"
              data-testid="deal-money-overpay"
              checked={allowOverpay}
              onChange={(event) => setAllowOverpay(event.target.checked)}
            />
            Разрешить переплату — сумма может превысить долг сознательно
          </label>

          <Button
            type="button"
            className="w-full"
            data-testid="deal-money-submit"
            disabled={!amountValid || selectedCurrency === null || paymentMutation.isPending}
            onClick={() => paymentMutation.mutate()}
          >
            {paymentMutation.isPending ? "Записываем…" : "Записать поступление"}
          </Button>
        </div>
      )}

      {ledger && ledger.payments.length > 0 ? (
        <div className="pwz-sheet p-4">
          <p className="pwz-eyebrow mb-1">Поступления</p>
          <ul className="divide-y divide-pad-200" data-testid="deal-money-payments">
            {ledger.payments.map((payment) => (
              <StatementRow
                key={payment.id}
                payment={payment}
                busy={reviewMutation.isPending}
                onReview={(decision) => reviewMutation.mutate({ paymentId: payment.id, decision })}
              />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
